// Common zod building blocks (S7) — shared by every module's request/response
// schemas. Keep this file free of any module-specific shape.
import { z } from 'zod';
/** Crockford base32 ULID: 26 chars, excludes I/L/O/U. */
const ULID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;
export const ulidSchema = z.string().regex(ULID_PATTERN, 'Invalid ULID');
/**
 * Money in integer ZAR cents. Accepts a native bigint (internal TS callers) or a
 * signed-integer numeric string (JSON transport, since JSON has no bigint type)
 * and normalizes to bigint.
 */
export const moneyCentsSchema = z
    .union([z.bigint(), z.string().regex(/^-?\d+$/, 'Invalid money cents value')])
    .transform((value) => (typeof value === 'bigint' ? value : BigInt(value)));
/** Mass/quantity as a decimal string (base UOM; max 3 dp — quantities are 3 dp
 * everywhere). Rejects >3 dp so sub-precision values can never reach the pool
 * math (Wave 2 gate 2026-07-07). */
export const quantitySchema = z
    .string()
    .regex(/^-?\d+(\.\d{1,3})?$/, 'Invalid decimal quantity (max 3 decimal places)');
/** Moisture percent, 0-100, as a decimal string. */
export const moisturePctSchema = z
    .string()
    .regex(/^\d+(\.\d+)?$/, 'Invalid moisture percent')
    .refine((v) => Number(v) >= 0 && Number(v) <= 100, 'mcPct must be within 0-100');
/** Cursor-based pagination request, shared by every list endpoint. */
export const paginationRequestSchema = z.object({
    cursor: z.string().optional(),
    limit: z.number().int().positive().max(200).default(50),
});
/** Cursor-based pagination response wrapper, parameterized by the row schema. */
export function paginationResponseSchema(itemSchema) {
    return z.object({
        items: z.array(itemSchema),
        nextCursor: z.string().nullable(),
    });
}
/** Standard error envelope (§10) returned by every API error response. */
export const errorEnvelopeSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
/**
 * SINGLE SOURCE OF TRUTH for one line of `pack_specs.packaging` jsonb (BUILD_SPEC.md
 * §5.2: "packaging jsonb [(product_id, qty)]" — e.g. a bag or carton consumed per
 * pack). Lives here in common.ts (unified 2026-07-07, Wave 4 gate) because BOTH the
 * config module's pack-spec WRITE endpoint (schemas/config.ts, `PUT /config/products/
 * :id/pack-spec`) and the packconv module's POST-time READ (re-exported from
 * schemas/packconv.ts, consumed by services/api/src/modules/packconv/conversions.ts)
 * must agree on the exact shape. They previously drifted — config typed it as an
 * OBJECT (`z.record`), which REJECTS the array shape packconv parses, so packaging
 * could only ever persist null/empty and pack conversions silently never booked the
 * packaging-consumable ISSUE movement. Defining it once here makes that drift
 * structurally impossible. `qty` is unsigned, max 3dp, strictly positive (quantity of
 * this packaging consumable used per SINGLE pack — multiplied by `numPacks` at post
 * time to get the total ISSUE quantity relieved). */
const packagingLineQtySchema = z
    .string()
    .regex(/^\d+(\.\d{1,3})?$/, 'Invalid decimal quantity (max 3 decimal places)')
    .refine((v) => Number(v) > 0, 'qty must be greater than zero');
export const packSpecPackagingLineSchema = z.object({
    productId: ulidSchema,
    qty: packagingLineQtySchema,
});
//# sourceMappingURL=common.js.map